import { FC, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

import { ChangeEmailModel } from '@/core/interfaces/exchange'
import { changeEmailStatusSelector } from '@/core/services/App/AppSelector'
import {
  changeEmailCodeThunk,
  changeEmailStatusThunk,
  changeEmailThunk,
  deleteChangeEmailThunk,
} from '@/core/services/App/AppSlice'
import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import { cn } from '@bem-react/classname'
import { yupResolver } from '@hookform/resolvers/yup'

import { useHandleServerError } from '@/hooks/useHandleServerError'

import { Field } from '@/components/Form/Field'
import { VerifyCode } from '@/components/Form/VerifyCode'
import { Button } from '@/components/UI/Button'

import { declensionOfNouns } from '@/utils/declensionOfNouns'
import yup from '@/utils/yup-extended'

import './FormEmail.scss'

const cnFormEmail = cn('FormEmail')

interface IFormEmailProps {
  email: string
  className?: string
  onSuccess?: () => void
}

export const FormEmail: FC<IFormEmailProps> = ({
  email,
  className,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { handleServerError } = useHandleServerError()

  const status = useAppSelector(changeEmailStatusSelector)

  const schema = yup.object().shape({
    email: yup.string().email().required(),
  })

  const {
    handleSubmit,
    register,
    reset,
    setFocus,
    formState: { isSubmitting },
  } = useForm<ChangeEmailModel>({
    defaultValues: {
      email: '',
    },
    resolver: yupResolver(schema),
  })

  const [action, setAction] = useState<
    'edit' | 'enter-code-old' | 'enter-code-new' | 'default'
  >('default')
  const [label, setLabel] = useState<string>(t('settings-your-email'))
  const [button, setButton] = useState<string>(t('settings-edit'))
  const [isCanceled, setIsCanceled] = useState<boolean>(false)

  useEffect(() => {
    if (status?.is_active === 1) {
      if (status.change?.step === '1') {
        setAction('enter-code-old')
      }

      if (status.change?.step === '2') {
        setAction('enter-code-new')
      }
    }

    if (status?.disabled_change === 1) {
      setAction('default')
      onSuccess?.()
    }
  }, [status])

  useEffect(() => {
    switch (action) {
      case 'edit':
        setLabel(t('settings-new-email'))
        setButton(t('settings-send-code'))
        reset({ email: '' })
        setFocus('email', { shouldSelect: true })
        break
      case 'enter-code-old':
        setLabel(t('settings-enter-code'))
        break
      case 'default':
        setLabel(t('settings-your-email'))
        setButton(t('settings-edit'))
        break
    }
  }, [action, t, reset, setFocus])

  useEffect(() => {
    if (email && action === 'default') {
      reset({ email })
    }
  }, [email, action, reset])

  useEffect(() => {
    changeEmailStatus()
  }, [])

  const timerDays = (days: number) => {
    return declensionOfNouns(days, [
      t('settings-time-reset-day1'),
      t('settings-time-reset-day2'),
      t('settings-time-reset-day3'),
    ])
  }

  const onSubmit = async (data: ChangeEmailModel) => {
    try {
      const res = await dispatch(
        changeEmailThunk({ email: data.email }),
      ).unwrap()

      if (res?.timer_days) {
        toast.success(
          t('settings-time-reset', { timer: timerDays(res.timer_days) }),
        )
      }

      if (res.is_create) {
        setAction('enter-code-old')
        toast.success(t('settings-code-enter-old-email'))
      }
    } catch (error) {
      handleServerError(error)
    }
  }

  const onHandleChangeCode = async (value: string) => {
    try {
      const res = await dispatch(changeEmailCodeThunk({ code: value })).unwrap()

      await changeEmailStatus()

      if (res?.step === '1') {
        toast.success(t('settings-code-enter-new-email'))
      }

      if (res?.is_change && res?.is_confirm_code === 1) {
        toast.success(
          t('settings-email-changed', {
            timer: timerDays(res.timer_days),
          }),
        )
      }
    } catch (error) {
      handleServerError(error)
    }
  }

  const changeEmailStatus = async () => {
    try {
      await dispatch(changeEmailStatusThunk()).unwrap()
    } catch (error) {
      handleServerError(error)
    }
  }

  const onHandleChange = (value: string) => {
    if (value.length === 6 && Number(value) > 0) onHandleChangeCode(value)
  }

  const canceled = async () => {
    setIsCanceled(true)
    try {
      await dispatch(deleteChangeEmailThunk()).unwrap()
      await changeEmailStatus()
      onSuccess?.()
      setIsCanceled(false)
      setAction('default')
    } catch (error) {
      handleServerError(error)
      setIsCanceled(false)
    }
  }

  return (
    <form
      className={cnFormEmail(null, [className])}
      onSubmit={handleSubmit(onSubmit)}
    >
      {action === 'enter-code-old' || action === 'enter-code-new' ? (
        <VerifyCode
          onChange={value => {
            onHandleChange(value)
          }}
          autoFocus
          placeholder=''
          key={action}
        />
      ) : (
        <Field
          label={label}
          mb='none'
          {...register('email')}
          disabled={action === 'default'}
        >
          <Button
            color='blue'
            size='sm'
            className={cnFormEmail('button')}
            onClick={action === 'default' ? () => setAction('edit') : undefined}
            type={action === 'default' ? 'button' : 'submit'}
            isLoading={isSubmitting}
          >
            {t(button)}
          </Button>
        </Field>
      )}

      {action !== 'default' && action !== 'edit' && (
        <div className={cnFormEmail('cancel')}>
          <Button
            color='blue'
            size='sm'
            onClick={canceled}
            isLoading={isCanceled}
          >
            {t('settings-cancel-change')}
          </Button>
        </div>
      )}
    </form>
  )
}
